import type { GridColDef } from "@mui/x-data-grid";

import type { ParameterPercentage, VitalConfig, VitalReading, VitalStats } from "@/components/trends";
import { UNIT_G, UNIT_KG, UNIT_LBS } from "@/utils/fhir-constants";
import { extractReadingContext } from "@/utils/fhir-observation-utils";
import type { WeightParameters } from "@/utils/goal-utils";

import type { fhirR4 } from "@smile-cdr/fhirts";

export const WEIGHT_LOINC = "29463-7";

export const KG_TO_LBS = 2.20462;
export const G_TO_LBS = 0.00220462;

export function convertToLbs(value: number, unitCode: string | undefined): number {
  switch (unitCode) {
    case UNIT_KG.code:
      return value * KG_TO_LBS;
    case UNIT_G.code:
      return value * G_TO_LBS;
    case UNIT_LBS.code:
    default:
      // Assume value is already in lbs when unit code is absent or unrecognized
      return value;
  }
}

const PARAMETER_COLORS = {
  inRange: "success.main",
  outOfRange: "error.dark",
} as const;

const VALUE_COLORS = {
  average: "text.primary",
  high: "text.primary",
  low: "text.primary",
} as const;

function formatWeight(value: number): string {
  return `${value.toFixed(1)} lbs`;
}

function extractReading(obs: fhirR4.Observation): VitalReading | undefined {
  if (!obs.effectiveDateTime) {
    return undefined;
  }
  const weight = obs.valueQuantity?.value;
  if (weight === undefined) {
    return undefined;
  }
  const unitCode = obs.valueQuantity?.code;
  return {
    date: new Date(obs.effectiveDateTime),
    observationId: obs.id ?? "",
    values: { weight: convertToLbs(weight, unitCode) },
    readingContext: extractReadingContext(obs),
  };
}

function computeWeightParameterPercentages(
  readings: VitalReading[],
  goals: WeightParameters | undefined
): ParameterPercentage[] {
  if (!goals || readings.length === 0) {
    return [];
  }

  const { minimum, maximum } = goals;
  if (minimum === undefined || maximum === undefined) {
    return [];
  }

  let inRange = 0;
  let outOfRange = 0;

  for (const reading of readings) {
    const weight = reading.values.weight;
    if (weight >= minimum && weight <= maximum) {
      inRange++;
    } else {
      outOfRange++;
    }
  }

  const total = readings.length;
  const percentages: ParameterPercentage[] = [];

  if (inRange > 0) {
    percentages.push({
      level: "inRange",
      percentage: Math.round((inRange / total) * 100),
      color: PARAMETER_COLORS.inRange,
    });
  }
  if (outOfRange > 0) {
    percentages.push({
      level: "outOfRange",
      percentage: Math.round((outOfRange / total) * 100),
      color: PARAMETER_COLORS.outOfRange,
    });
  }

  return percentages;
}

function computeWeightStats(readings: VitalReading[], goals: WeightParameters | undefined): VitalStats {
  if (readings.length === 0) {
    return {
      metricCards: [
        { labelKey: "trends.common.average", value: "-- lbs", valueColor: VALUE_COLORS.average },
        { labelKey: "trends.common.high", value: "-- lbs", valueColor: VALUE_COLORS.high },
        { labelKey: "trends.common.low", value: "-- lbs", valueColor: VALUE_COLORS.low },
      ],
      totalReadings: 0,
      parameterPercentages: [],
      parametersTooltipKey: "trends.common.parametersTooltip",
      parametersLabelKey: "trends.common.inParameters",
    };
  }

  const weightValues = readings.map(r => r.values.weight);
  const avg = weightValues.reduce((a, b) => a + b, 0) / weightValues.length;

  return {
    metricCards: [
      {
        labelKey: "trends.common.average",
        value: formatWeight(avg),
        valueColor: VALUE_COLORS.average,
      },
      {
        labelKey: "trends.common.high",
        value: formatWeight(Math.max(...weightValues)),
        valueColor: VALUE_COLORS.high,
      },
      {
        labelKey: "trends.common.low",
        value: formatWeight(Math.min(...weightValues)),
        valueColor: VALUE_COLORS.low,
      },
    ],
    totalReadings: readings.length,
    parameterPercentages: computeWeightParameterPercentages(readings, goals),
    parametersTooltipKey: "trends.common.parametersTooltip",
    parametersLabelKey: "trends.common.inParameters",
  };
}

function tableColumns(translate: (key: string) => string): GridColDef[] {
  return [
    {
      field: "weight",
      headerName: translate("trends.weight.weight"),
      flex: 1,
      valueFormatter: (value: number) => `${value.toFixed(1)} lbs`,
    },
  ];
}

export function buildWeightConfig(goals?: WeightParameters): VitalConfig {
  const hasRanges = goals?.minimum !== undefined && goals?.maximum !== undefined;

  return {
    id: "weight",
    titleKey: "trends.weight.title",
    metricName: "weight",
    loincCode: WEIGHT_LOINC,
    datasets: [{ key: "weight", labelKey: "trends.weight.title", color: p => p.primary.main }],
    normalRanges: hasRanges ? [{ lower: goals.minimum!, upper: goals.maximum! }] : undefined,
    tableColumns,
    extractReading,
    computeStats: readings => computeWeightStats(readings, goals),
  };
}
